import { create } from 'zustand';
import api from '../lib/api';

interface Repository {
    id: string;
    name: string;
    provider: string;
    url: string;
    branch: string;
    status: string;
}

interface FinOpsSummary {
    total: string;
    trend: string;
    details: { label: string; val: string }[];
}

interface Pipeline {
    id: string;
    project: string;
    branch: string;
    author: string;
    status: string;
    duration: string;
    time: string;
}

interface DevOpsState {
    repositories: Repository[];
    pipelines: Pipeline[];
    finopsSummary: FinOpsSummary | null;
    loading: boolean;
    error: string | null;
    fetchRepositories: () => Promise<void>;
    fetchPipelines: () => Promise<void>;
    fetchFinOpsSummary: () => Promise<void>;
    connectRepository: (data: any) => Promise<void>;
    deleteRepository: (id: string) => Promise<void>;
    syncRepository: (id: string) => Promise<{ success: boolean; message: string }>;
}

export const useDevOpsStore = create<DevOpsState>((set) => ({
    repositories: [],
    pipelines: [],
    finopsSummary: null,
    loading: false,
    error: null,

    fetchRepositories: async () => {
        set({ loading: true, error: null });
        try {
            const response = await api.get('/work/devops/repositories');
            set({ repositories: response.data, loading: false });
        } catch (error: any) {
            set({ error: error.message, loading: false });
        }
    },

    fetchPipelines: async () => {
        set({ loading: true, error: null });
        try {
            const response = await api.get('/work/devops/pipelines');
            set({ pipelines: response.data, loading: false });
        } catch (error: any) {
            set({ error: error.message, loading: false });
        }
    },

    fetchFinOpsSummary: async () => {
        set({ loading: true, error: null });
        try {
            const response = await api.get('/work/devops/finops/summary');
            set({ finopsSummary: response.data, loading: false });
        } catch (error: any) {
            set({ error: error.message, loading: false });
        }
    },

    connectRepository: async (data: any) => {
        set({ loading: true, error: null });
        try {
            const response = await api.post('/work/devops/repositories', data);
            set(state => ({
                repositories: [response.data, ...state.repositories],
                loading: false
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.error || error.message, loading: false });
            throw error;
        }
    },

    deleteRepository: async (id: string) => {
        try {
            await api.delete(`/work/devops/repositories/${id}`);
            set(state => ({ repositories: state.repositories.filter(r => r.id !== id) }));
        } catch (error: any) {
            set({ error: error.message });
            throw error;
        }
    },

    syncRepository: async (id: string) => {
        set({ loading: true });
        try {
            const res = await api.post(`/work/devops/repositories/${id}/sync`);
            set({ loading: false });
            return res.data as { success: boolean; message: string };
        } catch (error: any) {
            set({ error: error.message, loading: false });
            return { success: false, message: error.response?.data?.message || 'Sync failed' };
        }
    }
}));
